<template>
  <div>
    <v-toolbar dense flat color="grey lighten-3" class="scroll">
      <v-btn v-for="(link, i) in links" :key="i" text exact :to="link.path">{{
        link.name
      }}</v-btn>
      <v-spacer></v-spacer>
      <div class="user-selection-container" v-if="isTeamPage()">
        <v-select
          class="mr-3"
          :items="teams"
          v-model="selectedTeam"
          item-value="id"
          item-text="name"
          v-if="shouldShowTeamSelector"
        ></v-select>
      </div>
      <div class="user-selection-container">
        <v-autocomplete
          v-model="imitatedUser"
          :items="users"
          item-value="id"
          item-text="fullName"
          v-if="shouldShowUserSelector"
        >
        </v-autocomplete>
      </div>
    </v-toolbar>
    <slot></slot>
  </div>
</template>

<script>
import get from "lodash/get";
import sortBy from "lodash/sortBy";
import filter from "lodash/filter";
import { mapGetters } from "vuex";
import { hasRole } from "../util";

export default {
  computed: {
    ...mapGetters(["currentUser", "imitatedUser"]),
    users() {
      if (!this.currentUser) return [];

      // Admins see all organization users
      if (this.isAdminOrAttorney()) {
        let users = filter(this.currentUser.organization.users, u => u.active);
        users = users.map(u => {
          return Object.assign({}, u, {
            fullName: `${u.firstName} ${u.lastName}`
          });
        });
        return sortBy(users, ["firstName", "lastName"]);
      }

      // Non-admins see only themselves + their viewable users
      let allowedIds = [this.currentUser.id];
      if (this.currentUser.viewableUsers) {
        allowedIds = allowedIds.concat(
          this.currentUser.viewableUsers.map(u => u.id)
        );
      }

      let users = filter(this.currentUser.organization.users, u => {
        return u.active && allowedIds.includes(u.id);
      });
      users = users.map(u => {
        return Object.assign({}, u, {
          fullName: `${u.firstName} ${u.lastName}`
        });
      });
      return sortBy(users, ["firstName", "lastName"]);
    },
    shouldShowUserSelector() {
      if (!this.currentUser) return false;
      // Admins can always see the selector
      if (this.isAdminOrAttorney()) return true;
      // Non-admins can see it if they have viewable users assigned
      return (
        this.currentUser.viewableUsers &&
        this.currentUser.viewableUsers.length > 0
      );
    },
    teams() {
      if (!this.currentUser) return [];
      // Admin users see all organization teams
      if (this.isAdminOrAttorney()) {
        let teams = filter(this.currentUser.organization.teams, t => t.active);
        return sortBy(teams, ["name"]);
      }
      // Non-admin users only see teams they belong to
      let userTeamIds = (this.currentUser.teams || []).map(t => t.id);
      let teams = filter(this.currentUser.organization.teams, t => {
        return t.active && userTeamIds.includes(t.id);
      });
      return sortBy(teams, ["name"]);
    },
    shouldShowTeamSelector() {
      if (!this.currentUser) return false;
      // Admins can always see the selector
      if (this.isAdminOrAttorney()) return true;
      // Non-admin users can see it if they belong to multiple teams
      return this.currentUser.teams && this.currentUser.teams.length > 1;
    },
    imitatedUser: {
      get() {
        return this.$store.getters.imitatedUser;
      },
      set(value) {
        this.$store.dispatch("setImitatedUser", value);
      }
    },
    selectedTeam: {
      get() {
        return this.$store.getters.selectedTeam;
      },
      set(value) {
        this.$store.dispatch("setSelectedTeam", value);
      }
    }
  },
  props: ["links"],
  mounted() {
    if (!this.imitatedUser) {
      let imitatedUser = get(this.currentUser, "id");
      this.$store.dispatch("setImitatedUser", imitatedUser);
    } else {
      // Validate that stored imitated user is accessible to this user
      this.validateImitatedUser();
    }
    if (!this.selectedTeam) {
      let selectedTeam = get(this.currentUser, "teams[0].id");
      this.$store.dispatch("setSelectedTeam", selectedTeam);
    } else {
      // Validate that stored team is accessible to this user
      this.validateSelectedTeam();
    }
  },
  methods: {
    isTeamPage() {
      return (
        this.$route.name === "inventory-team" ||
        this.$route.name === "fees-list" ||
        this.$route.name === "fees-settlements" ||
        this.$route.name === "org-fees-list" ||
        this.$route.name === "org-fees-settlements" ||
        this.$route.name === "demands-list" ||
        this.$route.name === "demands-overdue" ||
        this.$route.name === "demands-index" ||
        this.$route.name === "cm-scorecard" ||
        this.$route.name === "dash-cm-scorecard"
      );
    },
    isAdminOrAttorney() {
      if (get(this.currentUser, "roles")) {
        return hasRole(this.currentUser, "admin");
        // || hasRole(this.currentUser, "attorney")
      } else {
        return false;
      }
    },
    validateSelectedTeam() {
      if (!this.currentUser) return;
      // Admins can access any team
      if (this.isAdminOrAttorney()) return;
      // Check if selected team is one the user belongs to
      const userTeamIds = (this.currentUser.teams || []).map(t => t.id);
      if (!userTeamIds.includes(this.selectedTeam)) {
        // Reset to user's first team if current selection is invalid
        let defaultTeam = get(this.currentUser, "teams[0].id");
        this.$store.dispatch("setSelectedTeam", defaultTeam);
      }
    },
    validateImitatedUser() {
      if (!this.currentUser) return;
      // Admins can access any user
      if (this.isAdminOrAttorney()) return;
      // Check if imitated user is self or one the user can view
      let allowedIds = [this.currentUser.id];
      if (this.currentUser.viewableUsers) {
        allowedIds = allowedIds.concat(
          this.currentUser.viewableUsers.map(u => u.id)
        );
      }
      if (!allowedIds.includes(this.imitatedUser)) {
        // Reset to self if current selection is invalid
        this.$store.dispatch("setImitatedUser", this.currentUser.id);
      }
    }
  }
};
</script>

<style scoped>
.scroll {
  overflow-x: auto;
  overflow-y: hidden;
}
.user-selection-container {
  width: 200px;
  height: 48px;
}
</style>
